const HALM_CART_SECTIONS = ["cart-drawer", "cart-icon-bubble"];
const HALM_OPEN_CART_DRAWER_EVENT = "halm:open-cart-drawer";
const HALM_FERTILIZER_PRODUCT_ID = "15582615568710";
const HALM_FERTILIZER_SKUS = ["1001", "1002"];
const HALM_AI_SOIL_ANALYSIS_SKU = "vHalmBodenanalyseKI";
const getCartDrawer = () => document.querySelector("halm-cart-drawer");

const getBackgroundBlurElements = (drawer) =>
  [...document.body.children].filter(
    (element) =>
      element !== drawer &&
      !element.contains(drawer) &&
      !["SCRIPT", "STYLE", "LINK"].includes(element.tagName),
  );

const getSectionInnerHTML = (html, selector) => {
  return new DOMParser()
    .parseFromString(html, "text/html")
    .querySelector(selector);
};

const requestCart = async (url, body) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Cart request failed with status ${response.status}`);
  }

  return response.json();
};

class HalmCartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector("#CartDrawer");
    this.errorContainer = this.querySelector("#CartDrawer-CartErrors");

    this.addEventListener("click", this.handleClick);
    this.addEventListener("keyup", this.handleKeyup);

    this.cleanupInvalidItems();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("keyup", this.handleKeyup);
    document.removeEventListener("click", this.handleOutsideClick, true);
  }

  handleClick = (event) => {
    const closeTrigger = event.target.closest("[data-halm-cart-close]");
    if (closeTrigger) {
      event.preventDefault();
      this.close();
      return;
    }

    const removeTrigger = event.target.closest("[data-halm-cart-remove]");
    if (removeTrigger) {
      event.preventDefault();
      this.removeLine(removeTrigger);
      return;
    }

    const minusTrigger = event.target.closest(
      "[data-halm-cart-quantity-minus]",
    );
    if (minusTrigger) {
      event.preventDefault();
      const quantity = Math.max(
        Number(minusTrigger.dataset.quantity || 1) - 1,
        0,
      );
      this.changeLine(minusTrigger.dataset.lineKey, quantity);
      return;
    }

    const plusTrigger = event.target.closest("[data-halm-cart-quantity-plus]");
    if (plusTrigger) {
      event.preventDefault();
      const quantity = Number(plusTrigger.dataset.quantity || 0) + 1;
      this.changeLine(plusTrigger.dataset.lineKey, quantity);
      return;
    }
  };

  handleKeyup = (event) => {
    if (event.code === "Escape") {
      this.close();
    }
  };

  handleOutsideClick = (event) => {
    if (!this.classList.contains("is-active")) return;

    const eventPath = event.composedPath?.() ?? [];
    if (this.panel && eventPath.includes(this.panel)) return;

    this.close();
  };

  open() {
    this.classList.add("is-active");
    this.setAttribute("aria-hidden", "false");
    document.body.classList.add("overflow-hidden");
    document.body.classList.add("halm-cart-drawer-open");
    getBackgroundBlurElements(this).forEach((element) => {
      element.classList.add("halm-cart-drawer__background-blur");
    });
    window.clearTimeout(this.outsideClickListenerTimeout);
    this.outsideClickListenerTimeout = window.setTimeout(() => {
      if (this.classList.contains("is-active")) {
        document.addEventListener("click", this.handleOutsideClick, true);
      }
    }, 0);
    const viewCartTracking = window.HalmAblyftEvents?.trackViewCart?.();
    viewCartTracking?.catch?.((error) => {
      console.warn("Could not track view cart goal", error);
    });

    window.requestAnimationFrame(() => {
      this.panel?.focus();
    });
  }

  close() {
    this.classList.remove("is-active");
    this.setAttribute("aria-hidden", "true");
    document.body.classList.remove("overflow-hidden");
    document.body.classList.remove("halm-cart-drawer-open");
    window.clearTimeout(this.outsideClickListenerTimeout);
    document.removeEventListener("click", this.handleOutsideClick, true);
    document
      .querySelectorAll(".halm-cart-drawer__background-blur")
      .forEach((element) => {
        element.classList.remove("halm-cart-drawer__background-blur");
      });
  }

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);
    this.querySelectorAll("button").forEach((button) => {
      if (isLoading) {
        button.dataset.previousDisabled = button.disabled ? "true" : "false";
        button.disabled = true;
      } else if (button.dataset.previousDisabled === "false") {
        button.disabled = false;
      }
    });
  }

  setError(message) {
    if (!this.errorContainer) return;
    this.errorContainer.textContent = message || "";
  }

  updateElementReferences() {
    this.panel = this.querySelector("#CartDrawer");
    this.errorContainer = this.querySelector("#CartDrawer-CartErrors");
  }

  getSectionsRequestPayload() {
    return {
      sections: HALM_CART_SECTIONS,
      sections_url: window.location.pathname,
    };
  }

  async changeLine(lineKey, quantity) {
    if (!lineKey) return;

    try {
      this.setError("");
      this.setLoading(true);
      const response = await requestCart(window.routes.cart_change_url, {
        id: lineKey,
        quantity,
        ...this.getSectionsRequestPayload(),
      });
      this.renderSections(response.sections, {
        open: this.classList.contains("is-active"),
      });
    } catch (error) {
      console.error(error);
      this.setError("Der Warenkorb konnte nicht aktualisiert werden.");
      this.setLoading(false);
    }
  }

  async removeLine(trigger) {
    const lineKey = trigger.dataset.lineKey;
    if (!lineKey) return;

    const shouldRemoveAiSoilAnalysis =
      String(trigger.dataset.productId) === HALM_FERTILIZER_PRODUCT_ID &&
      HALM_FERTILIZER_SKUS.includes(trigger.dataset.sku);
    const aiSoilAnalysisLineKey = shouldRemoveAiSoilAnalysis
      ? this.querySelector(`[data-sku="${HALM_AI_SOIL_ANALYSIS_SKU}"]`)?.dataset
          .lineKey
      : null;
    const operations = [
      { id: lineKey, quantity: 0 },
      ...(aiSoilAnalysisLineKey && aiSoilAnalysisLineKey !== lineKey
        ? [{ id: aiSoilAnalysisLineKey, quantity: 0 }]
        : []),
    ];

    try {
      this.setError("");
      this.setLoading(true);
      let response;
      for (const [index, operation] of operations.entries()) {
        response = await requestCart(window.routes.cart_change_url, {
          ...operation,
          ...(index === operations.length - 1
            ? this.getSectionsRequestPayload()
            : {}),
        });
      }
      this.renderSections(response.sections, {
        open: this.classList.contains("is-active"),
      });
    } catch (error) {
      console.error(error);
      this.setError("Der Warenkorb konnte nicht aktualisiert werden.");
      this.setLoading(false);
    }
  }

  async cleanupInvalidItems() {
    const invalidLineKeys = [
      ...this.querySelectorAll("[data-halm-invalid-line-key]"),
    ]
      .map((element) => element.dataset.halmInvalidLineKey)
      .filter(Boolean);
    const normalizedLineItems = [
      ...this.querySelectorAll("[data-halm-normalize-line-key]"),
    ]
      .map((element) => ({
        lineKey: element.dataset.halmNormalizeLineKey,
        quantity: Number(element.dataset.halmNormalizeQuantity || 1),
      }))
      .filter(({ lineKey }) => Boolean(lineKey));
    const cartOperations = [
      ...invalidLineKeys.map((lineKey) => ({
        lineKey,
        quantity: 0,
      })),
      ...normalizedLineItems,
    ];

    if (!cartOperations.length || this.dataset.cleanupRunning === "true")
      return;

    try {
      this.dataset.cleanupRunning = "true";
      let response;
      for (const [index, operation] of cartOperations.entries()) {
        response = await requestCart(window.routes.cart_change_url, {
          id: operation.lineKey,
          quantity: operation.quantity,
          ...(index === cartOperations.length - 1
            ? this.getSectionsRequestPayload()
            : {}),
        });
      }
      this.renderSections(response.sections, {
        open: this.classList.contains("is-active"),
      });
      this.dataset.cleanupRunning = "false";
    } catch (error) {
      console.error(error);
      this.dataset.cleanupRunning = "false";
    }
  }

  async refresh({ open = false } = {}) {
    try {
      const sectionUrl = new URL(
        window.routes.cart_url || "/cart",
        window.location.origin,
      );
      sectionUrl.searchParams.set("sections", HALM_CART_SECTIONS.join(","));
      sectionUrl.searchParams.set("_", String(Date.now()));

      const response = await fetch(sectionUrl.toString(), {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(
          `Cart section refresh failed with status ${response.status}`,
        );
      }

      const sections = await response.json();
      this.renderSections(sections, { open });
    } catch (error) {
      console.error(error);
      if (open) {
        this.open();
      }
    }
  }

  renderSections(sections, { open = false } = {}) {
    if (!sections) {
      this.setLoading(false);
      if (open) {
        this.open();
      }
      return;
    }

    let didReplaceDrawer = false;
    const cartDrawerHtml = sections["cart-drawer"];
    if (cartDrawerHtml) {
      const nextDrawer = getSectionInnerHTML(
        cartDrawerHtml,
        "halm-cart-drawer",
      );
      if (nextDrawer) {
        didReplaceDrawer = true;
        if (this.classList.contains("is-active")) {
          const bodyScrollTop =
            this.querySelector(".halm-cart-drawer__body")?.scrollTop ?? 0;

          this.innerHTML = nextDrawer.innerHTML;
          this.className = nextDrawer.className;
          this.classList.add("is-active");
          this.setAttribute("aria-hidden", "false");
          this.updateElementReferences();
          this.setLoading(false);
          this.cleanupInvalidItems();

          const nextBody = this.querySelector(".halm-cart-drawer__body");
          if (nextBody) {
            nextBody.scrollTop = bodyScrollTop;
          }
        } else {
          this.replaceWith(nextDrawer);
          if (open) {
            window.requestAnimationFrame(() => {
              const drawer = getCartDrawer();
              drawer?.open();
            });
          }
        }
      }
    }

    if (open && !didReplaceDrawer) {
      this.open();
    }

    const cartIconBubbleHtml = sections["cart-icon-bubble"];
    if (cartIconBubbleHtml) {
      const nextCartIconBubble = getSectionInnerHTML(
        cartIconBubbleHtml,
        "#cart-icon-bubble",
      );
      const currentCartIconBubble = document.querySelector("#cart-icon-bubble");
      if (nextCartIconBubble && currentCartIconBubble) {
        currentCartIconBubble.replaceWith(nextCartIconBubble);
      }
    }
  }
}

if (!customElements.get("halm-cart-drawer")) {
  customElements.define("halm-cart-drawer", HalmCartDrawer);
}

window.HalmCartDrawer = {
  open: ({ sections } = {}) => {
    const drawer = getCartDrawer();
    if (!drawer) {
      window.location.href = window.routes.cart_url || "/cart";
      return;
    }

    if (sections) {
      drawer.renderSections(sections, { open: true });
      return;
    }

    drawer.refresh({ open: true });
  },
  refresh: () => getCartDrawer()?.refresh(),
};

window.addEventListener(HALM_OPEN_CART_DRAWER_EVENT, (event) => {
  if (event.detail) {
    event.detail.handled = true;
  }

  window.HalmCartDrawer.open({ sections: event.detail?.sections });
});
